import { useState, useRef, useEffect, useCallback } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { loginRequest } from "./authConfig";

const API = "https://warenlogistik-api.azurewebsites.net/api";
const ADMIN_GROUP = "5001ae6b-2960-4a8c-94d7-048667f4f98b";
const ALLOWED_GROUPS = ["5001ae6b-2960-4a8c-94d7-048667f4f98b", "903fb672-2348-4733-af9f-9ba94fb1f753"];
const initialForm = { vorgang: "Eingang", kunde: "", kundennummer: "", auftragsnummer: "", warentyp: "", warenbezeichnung: "", seriennummer: "", menge: 1, bemerkung: "" };
const BLUE = "#2196c4";
const SIDEBAR_W = 220;

const NAV = [
  { id: "dashboard",  label: "Dashboard",    icon: "⊞" },
  { id: "buchen",     label: "Neue Buchung", icon: "＋" },
  { id: "lager",      label: "Lagerbestand", icon: "◫" },
  { id: "historie",   label: "Historie",     icon: "≡" },
  { id: "kleinteile", label: "Kleinteile",   icon: "⬡" },
];
const NAV_ADMIN = { id: "admin", label: "Admin", icon: "⚙" };

const INPUT_STYLE = { width: "100%", boxSizing: "border-box", borderRadius: 8, border: "1px solid #dde1e7", padding: "9px 12px", fontSize: 14, outline: "none", background: "#fafbfc" };

function KundenCombobox({ kunden, value, onChange, placeholder = "Kunde suchen oder wählen…", style: extraStyle = {} }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(value || "");
  const ref = useRef();
  const inputRef = useRef();

  useEffect(() => {
    if (value !== undefined && value !== q && document.activeElement !== inputRef.current) {
      setQ(value || "");
    }
  }, [value]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = kunden.filter(k => k.name.toLowerCase().includes(q.toLowerCase())).slice(0, 20);

  return (
    <div ref={ref} style={{ position: "relative", ...extraStyle }}>
      <input
        ref={inputRef}
        value={q}
        onChange={e => { const val = e.target.value; setQ(val); setOpen(true); onChange({ name: val, kundennummer: "" }); }}
        onFocus={() => setOpen(true)}
        placeholder={placeholder}
        style={INPUT_STYLE}
        autoComplete="off"
      />
      {open && (filtered.length > 0 || q.length > 0) && (
        <div style={{ position: "absolute", top: "100%", left: 0, right: 0, background: "#fff", border: "1px solid #dde1e7", borderRadius: 8, zIndex: 999, maxHeight: 220, overflowY: "auto", boxShadow: "0 4px 16px rgba(0,0,0,0.12)", marginTop: 2 }}>
          {filtered.length === 0 && <div style={{ padding: "10px 14px", fontSize: 13, color: "#888" }}>Kein Kunde gefunden</div>}
          {filtered.map(k => (
            <div key={k.id}
              onMouseDown={e => { e.preventDefault(); setQ(k.name); onChange(k); setOpen(false); }}
              style={{ padding: "9px 14px", fontSize: 13, cursor: "pointer", borderBottom: "0.5px solid #f0f2f5" }}
              onMouseEnter={e => e.currentTarget.style.background = "#f0f7fb"}
              onMouseLeave={e => e.currentTarget.style.background = "#fff"}>
              <span style={{ fontWeight: 500 }}>{k.name}</span>
              {k.kundennummer && <span style={{ fontSize: 11, color: "#888", marginLeft: 8 }}>{k.kundennummer}</span>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function App() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const user = accounts[0] ? { name: accounts[0].name, email: accounts[0].username } : null;

  const [screen, setScreen] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [buchungen, setBuchungen] = useState([]);
  const [kunden, setKunden] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [filterKunde, setFilterKunde] = useState("");
  const [filterSuche, setFilterSuche] = useState("");
  const [fotoPreview, setFotoPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lagerbestand, setLagerbestand] = useState([]);
  const [lagerKunde, setLagerKunde] = useState("");
  const [lagerTypFilter, setLagerTypFilter] = useState("");
  const [schwellwerte, setSchwellwerte] = useState([]);
  const [neuerSchwellwert, setNeuerSchwellwert] = useState({ kunde: "", warentyp: "", minbestand: "" });
  const [neuerKunde, setNeuerKunde] = useState({ name: "", kundennummer: "" });
  const [warentypen, setWarentypen] = useState([]);
  const [neuerWarentyp, setNeuerWarentyp] = useState("");
  const [mailConfig, setMailConfig] = useState({ absender: "lagerbestand@artaker.it", empfaenger: [], empfaengerModus: "admins", empfaengerInput: "", csvVersandTag: "", softDeleteTage: "30" });
  const [adminEmails, setAdminEmails] = useState([]);
  const [kleinteile, setKleinteile] = useState([]);
  const [neuesKleinteil, setNeuesKleinteil] = useState("");
  const [kleinteilAusbuchungen, setKleinteilAusbuchungen] = useState([]);
  const [lieferscheinModal, setLieferscheinModal] = useState(null);
  const [ausbuchenModal, setAusbuchenModal] = useState(null);
  const [ausbuchenForm, setAusbuchenForm] = useState({ kunde: "", kundennummer: "", auftragsnummer: "", menge: 1, bemerkung: "" });
  const [geloeschteBuchungen, setGeloeschteBuchungen] = useState([]);
  const [deleteLog, setDeleteLog] = useState([]);
  const [apiKey, setApiKey] = useState(null);
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [editLog, setEditLog] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [kundenSuche, setKundenSuche] = useState("");
  const [kundenSeite, setKundenSeite] = useState(0);
  const KUNDEN_PRO_SEITE = 20;

  const [sammelModal, setSammelModal] = useState(null);
  const [sammelForm, setSammelForm] = useState({ kunde: "", kundennummer: "", auftragsnummer: "", bemerkung: "" });
  const [sammelAuswahl, setSammelAuswahl] = useState([]);
  const [schnellErfassung, setSchnellErfassung] = useState(false);

  const fileRef = useRef();
  const importRef = useRef();

  async function getToken() {
    const res = await instance.acquireTokenSilent({ scopes: ["User.Read"], account: accounts[0] });
    return res.accessToken;
  }

  async function apiFetch(path, options = {}) {
    const token = await getToken();
    const res = await fetch(`${API}${path}`, {
      ...options,
      headers: { "Authorization": `Bearer ${token}`, "Content-Type": "application/json", ...options.headers },
    });
    if (!res.ok) { const msg = await res.text(); throw new Error(msg || `API Fehler: ${res.status}`); }
    return res;
  }

  async function checkAdmin() {
    const token = await getToken();
    const res = await fetch("https://graph.microsoft.com/v1.0/me/memberOf", { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    const groups = data.value?.map(g => g.id) ?? [];
    if (!groups.some(id => ALLOWED_GROUPS.includes(id))) { alert("Kein Zugriff!"); instance.logoutRedirect(); return false; }
    setIsAdmin(groups.includes(ADMIN_GROUP));
    return groups.includes(ADMIN_GROUP);
  }

  async function login() { try { await instance.loginRedirect(loginRequest); } catch(e) { console.error(e); } }
  function logout() { instance.logoutRedirect(); }

  const loadBuchungen = useCallback(async (kunde = "", suche = "") => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (kunde) p.append("kunde", kunde);
      if (suche) p.append("suche", suche);
      const res = await apiFetch(`/buchungen${p.toString() ? "?" + p : ""}`);
      setBuchungen(await res.json());
    } catch(e) { setError(e.message); }
    setLoading(false);
  }, []);

  const loadKunden = useCallback(async () => {
    try { setKunden(await (await apiFetch("/kunden")).json()); } catch(e) { setError(e.message); }
  }, []);

  const loadLagerbestand = useCallback(async (k = "") => {
    try { setLagerbestand(await (await apiFetch(k ? `/lagerbestand?kunde=${encodeURIComponent(k)}` : "/lagerbestand")).json()); } catch(e) { setError(e.message); }
  }, []);

  const loadSchwellwerte = useCallback(async () => {
    try { setSchwellwerte(await (await apiFetch("/schwellwerte")).json()); } catch(e) { setError(e.message); }
  }, []);

  const loadKonfiguration = useCallback(async () => {
    try {
      const config = await (await apiFetch("/konfiguration")).json();
      const empfaenger = config.mailEmpfaenger ? JSON.parse(config.mailEmpfaenger) : [];
      setMailConfig({ absender: config.mailAbsender || "lagerbestand@artaker.it", empfaenger, empfaengerModus: config.mailEmpfaengerModus || "admins", empfaengerInput: empfaenger.join("; "), csvVersandTag: config.csvVersandTag || "", softDeleteTage: config.softDeleteTage || "30" });
    } catch(e) { console.error(e); }
  }, []);

  const loadAdminEmails = useCallback(async () => {
    try { const d = await (await apiFetch("/adminemails")).json(); setAdminEmails(d.emails || []); } catch(e) { console.error(e); }
  }, []);

  const loadKleinteile = useCallback(async () => {
    try { setKleinteile(await (await apiFetch("/kleinteile")).json()); } catch(e) { setError(e.message); }
  }, []);

  const loadKleinteilAusbuchungen = useCallback(async () => {
    try { setKleinteilAusbuchungen(await (await apiFetch("/kleinteil-ausbuchungen")).json()); } catch(e) { setError(e.message); }
  }, []);

  const loadWarentypen = useCallback(async () => {
    try { setWarentypen(await (await apiFetch("/warentypen")).json()); } catch(e) { setError(e.message); }
  }, []);

  const loadGeloeschteBuchungen = useCallback(async () => {
    try { setGeloeschteBuchungen(await (await apiFetch("/buchungen?geloescht=1")).json()); } catch(e) { console.error(e); }
  }, []);

  const loadDeleteLog = useCallback(async () => {
    try { setDeleteLog(await (await apiFetch("/deletelog")).json()); } catch(e) { console.error(e); }
  }, []);

  const loadApiKey = useCallback(async () => {
    try { const d = await (await apiFetch("/kunden/apikey")).json(); setApiKey(d.apiKey); } catch(e) { console.error(e); }
  }, []);

  const loadEditLog = useCallback(async () => {
    try { setEditLog(await (await apiFetch("/editlog")).json()); } catch(e) { console.error(e); }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      checkAdmin().then(admin => { if (admin) { loadGeloeschteBuchungen(); loadDeleteLog(); loadApiKey(); loadEditLog(); } });
      loadBuchungen(); loadKunden(); loadLagerbestand(); loadSchwellwerte();
      loadKonfiguration(); loadAdminEmails(); loadKleinteile(); loadKleinteilAusbuchungen();
      loadWarentypen();
    }
  }, [isAuthenticated]);

  async function handleFoto(e) {
    const file = e.target.files[0]; if (!file) return;
    setFotoPreview(URL.createObjectURL(file)); setScanning(true); setScanResult(null);
    const reader = new FileReader();
    reader.onload = async (ev) => {
      try {
        const res = await apiFetch("/scan", { method: "POST", body: JSON.stringify({ imageBase64: ev.target.result.split(",")[1], mediaType: file.type || "image/jpeg" }) });
        const parsed = await res.json(); setScanResult(parsed);
        setForm(f => ({ ...f, warentyp: parsed.warentyp||f.warentyp, warenbezeichnung: parsed.warenbezeichnung||f.warenbezeichnung, seriennummer: parsed.seriennummer||f.seriennummer, kunde: parsed.kunde||f.kunde, auftragsnummer: parsed.auftragsnummer||f.auftragsnummer }));
      } catch { setScanResult({ error: "Erkennung fehlgeschlagen" }); }
      setScanning(false);
    };
    reader.readAsDataURL(file);
  }

  async function handleSubmit() {
    if (!form.kunde || !form.warentyp) return;
    try {
      await apiFetch("/buchungen", { method: "POST", body: JSON.stringify(form) });
      setForm(initialForm); setFotoPreview(null); setScanResult(null);
      setSubmitted(true); setTimeout(() => setSubmitted(false), 2500);
      setScreen("dashboard"); await loadBuchungen(filterKunde, filterSuche); await loadLagerbestand(lagerKunde);
    } catch(e) { setError(e.message); }
  }

  async function handleAusbuchen(id, maxMenge) {
    let menge = maxMenge;
    if (maxMenge > 1) {
      const input = window.prompt(`Wie viele Stück ausbuchen? (max. ${maxMenge})`, maxMenge);
      if (!input) return; menge = parseInt(input);
      if (isNaN(menge) || menge < 1 || menge > maxMenge) { setError(`Ungültige Menge.`); return; }
    } else { if (!window.confirm("Gegenbuchung erstellen?")) return; }
    try {
      await apiFetch(`/buchungen/${id}/ausbuchen`, { method: "POST", body: JSON.stringify({ menge }) });
      await loadBuchungen(filterKunde, filterSuche); await loadLagerbestand(lagerKunde);
    } catch(e) { setError(e.message); }
  }

  async function handleDelete(id) {
    if (!window.confirm("Buchung löschen? Sie kann wiederhergestellt werden.")) return;
    try {
      await apiFetch(`/buchungen/${id}`, { method: "DELETE" });
      await loadBuchungen(filterKunde, filterSuche); await loadLagerbestand(lagerKunde); await loadGeloeschteBuchungen();
    } catch(e) { setError(e.message); }
  }

  async function handleImport(file) {
    if (!file) return;
    setImportLoading(true); setImportResult(null);
    try {
      let kunden = [];
      if (file.name.endsWith(".csv")) {
        const text = await file.text();
        const lines = text.split(/\r?\n/).filter(l => l.trim());
        const headers = lines[0].split(/[;,]/).map(h => h.replace(/"/g, "").trim().toLowerCase());
        const nameIdx = headers.findIndex(h => h.includes("name"));
        const knrIdx  = headers.findIndex(h => h.includes("nummer") || h.includes("nr"));
        for (let i = 1; i < lines.length; i++) {
          const cols = lines[i].split(/[;,]/).map(c => c.replace(/"/g, "").trim());
          const name = nameIdx >= 0 ? cols[nameIdx] : cols[0];
          const kundennummer = knrIdx >= 0 ? cols[knrIdx] : cols[1] || "";
          if (name) kunden.push({ name, kundennummer });
        }
      } else {
        const XLSX = await import("https://cdn.jsdelivr.net/npm/xlsx@0.18.5/xlsx.mjs");
        const buf = await file.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(ws, { defval: "" });
        for (const row of rows) {
          const nameKey = Object.keys(row).find(k => k.toLowerCase().includes("name"));
          const knrKey  = Object.keys(row).find(k => k.toLowerCase().includes("nummer") || k.toLowerCase().includes("nr"));
          const name = nameKey ? String(row[nameKey]).trim() : "";
          const kundennummer = knrKey ? String(row[knrKey]).trim() : "";
          if (name) kunden.push({ name, kundennummer });
        }
      }
      if (kunden.length === 0) { setError("Keine Kundendaten gefunden — prüfe Spaltenbezeichnungen (Name, Kundennummer)"); setImportLoading(false); return; }
      const result = await (await apiFetch("/kunden/import", { method: "POST", body: JSON.stringify({ kunden }) })).json();
      setImportResult(result);
      await loadKunden();
    } catch(e) { setError(e.message); }
    setImportLoading(false);
  }

  async function exportCSV() {
    const token = await getToken();
    const res = await fetch(`${API}/buchungen/export`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "lager-export.csv"; a.click();
  }

  const navigate = (id) => { setScreen(id); setSidebarOpen(false); };

  const s = {
    wrap:       { display: "flex", minHeight: "100vh", fontFamily: "'Segoe UI', Arial, sans-serif", background: "#f0f2f5", color: "#1a1a1a" },
    sidebar:    { width: SIDEBAR_W, background: "#0f1923", display: "flex", flexDirection: "column", position: "fixed", top: 0, left: 0, height: "100vh", zIndex: 100, transition: "transform 0.25s ease" },
    sidebarLogo:{ padding: "20px 20px 16px", borderBottom: "1px solid #1e2d3d", display: "flex", alignItems: "center", gap: 10 },
    navItem: (active) => ({ display: "flex", alignItems: "center", gap: 10, padding: "10px 20px", cursor: "pointer", fontSize: 14, color: active ? "#fff" : "#8a9bb0", background: active ? "rgba(33,150,196,0.18)" : "transparent", borderLeft: active ? `3px solid ${BLUE}` : "3px solid transparent", transition: "all 0.15s", borderRadius: "0 6px 6px 0", margin: "2px 8px 2px 0", userSelect: "none" }),
    navIcon:    { fontSize: 16, width: 20, textAlign: "center" },
    main:       { marginLeft: SIDEBAR_W, flex: 1, display: "flex", flexDirection: "column", minHeight: "100vh" },
    topbar:     { background: "#fff", borderBottom: "1px solid #e8eaed", padding: "0 24px", height: 56, display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 50 },
    content:    { padding: "24px", maxWidth: 800, width: "100%", margin: "0 auto", boxSizing: "border-box" },
    card:       { background: "#fff", borderRadius: 12, padding: "20px 24px", marginBottom: 16, boxShadow: "0 1px 4px rgba(0,0,0,0.07)" },
    cardTitle:  { fontWeight: 600, fontSize: 15, marginBottom: 16, color: "#1a1a1a" },
    label:      { fontSize: 12, fontWeight: 600, color: "#666", marginBottom: 4, display: "block", textTransform: "uppercase", letterSpacing: "0.04em" },
    input:      { width: "100%", boxSizing: "border-box", borderRadius: 8, border: "1px solid #dde1e7", padding: "9px 12px", fontSize: 14, outline: "none", transition: "border 0.15s", background: "#fafbfc" },
    row2:       { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 },
    row1:       { marginBottom: 14 },
    btn:        { padding: "8px 16px", borderRadius: 8, border: "1px solid #dde1e7", background: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 500, transition: "all 0.15s", color: "#444" },
    btnPrimary: { padding: "9px 20px", borderRadius: 8, border: "none", background: BLUE, color: "#fff", cursor: "pointer", fontSize: 14, fontWeight: 600, transition: "opacity 0.15s", boxShadow: "0 2px 6px rgba(33,150,196,0.3)" },
    btnDanger:  { padding: "5px 12px", borderRadius: 6, border: "1px solid #fcc", background: "#fff5f5", cursor: "pointer", fontSize: 12, color: "#c0392b", fontWeight: 500 },
    btnSmall:   { padding: "5px 12px", borderRadius: 6, border: "1px solid #dde1e7", background: "#fff", cursor: "pointer", fontSize: 12, fontWeight: 500 },
    btnOrange:  { padding: "6px 14px", borderRadius: 8, border: "none", background: "#e67e22", color: "#fff", cursor: "pointer", fontSize: 13, fontWeight: 600 },
    btnGhost:   { padding: "8px 16px", borderRadius: 8, border: "none", background: "transparent", cursor: "pointer", fontSize: 13, color: "#8a9bb0", transition: "color 0.15s" },
    tag: (v) => ({ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, letterSpacing: "0.03em", background: v === "Eingang" ? "#e8f5e0" : "#fdecea", color: v === "Eingang" ? "#2d7a11" : "#c0392b" }),
    badge:      { display: "inline-block", padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: "rgba(33,150,196,0.12)", color: BLUE },
    adminBadge: { display: "inline-block", padding: "2px 8px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: "#fff3cd", color: "#856404", marginLeft: 6 },
    metric:     { background: "linear-gradient(135deg, #1e2d3d 0%, #0f1923 100%)", borderRadius: 12, padding: "18px 20px", color: "#fff" },
    metricVal:  { fontSize: 28, fontWeight: 700, lineHeight: 1 },
    metricLbl:  { fontSize: 12, color: "#8a9bb0", marginTop: 4 },
    success:    { background: "#e8f5e0", color: "#2d7a11", borderRadius: 8, padding: "10px 16px", fontSize: 14, marginBottom: 16, display: "flex", alignItems: "center", gap: 8 },
    errorBox:   { background: "#fdecea", color: "#c0392b", borderRadius: 8, padding: "10px 16px", fontSize: 14, marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" },
    fotoBox:    { border: "2px dashed #dde1e7", borderRadius: 10, padding: 20, textAlign: "center", cursor: "pointer", marginBottom: 14, background: "#fafbfc", transition: "border 0.15s" },
    filterBar:  { display: "flex", gap: 10, marginBottom: 16, alignItems: "center", flexWrap: "wrap" },
    overlay:    { position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 },
    modal:      { background: "#fff", borderRadius: 14, padding: "28px", width: "100%", maxWidth: 440, boxShadow: "0 12px 40px rgba(0,0,0,0.2)", animation: "fadeIn 0.2s ease" },
    hamburger:  { display: "none", background: "none", border: "none", cursor: "pointer", fontSize: 22, color: "#444", padding: 4 },
  };

  const initials = (n) => n ? n.split(" ").map(w => w[0]).join("").slice(0,2).toUpperCase() : "?";

  const filterBar = (
    <div style={s.filterBar}>
      <select value={filterKunde} onChange={e => { setFilterKunde(e.target.value); loadBuchungen(e.target.value, filterSuche); }} style={{ ...s.input, width: "auto", minWidth: 140 }}>
        <option value="">Alle Kunden</option>
        {kunden.map(k => <option key={k.id} value={k.name}>{k.name}</option>)}
      </select>
      <input value={filterSuche} onChange={e => setFilterSuche(e.target.value)} onKeyDown={e => e.key === "Enter" && loadBuchungen(filterKunde, filterSuche)} placeholder="Suche: Artikel, SN, Auftrag…" style={{ ...s.input, flex: 1, minWidth: 160 }} />
      <button style={s.btnPrimary} onClick={() => loadBuchungen(filterKunde, filterSuche)}>Suchen</button>
      {(filterKunde || filterSuche) && <button style={s.btn} onClick={() => { setFilterKunde(""); setFilterSuche(""); loadBuchungen("",""); }}>✕</button>}
    </div>
  );

  const istRuecknahme = (b) => b.vorgang === "Eingang" && b.bemerkung?.startsWith("Gegenbuchung zu");

  const BuchungCard = ({ b }) => (
    <div style={{ ...s.card, marginBottom: 10, padding: "14px 18px", transition: "box-shadow 0.15s" }}
      onMouseEnter={e => e.currentTarget.style.boxShadow = "0 3px 10px rgba(0,0,0,0.1)"}
      onMouseLeave={e => e.currentTarget.style.boxShadow = "0 1px 4px rgba(0,0,0,0.07)"}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={s.tag(b.vorgang)}>{b.vorgang}</span>
          {istRuecknahme(b) && (
            <span style={{ display: "inline-flex", alignItems: "center", padding: "3px 10px", borderRadius: 20, fontSize: 11, fontWeight: 600, background: "#f0f2f5", color: "#666" }}>↩ Rücknahme</span>
          )}
          <span style={{ fontWeight: 600, fontSize: 14 }}>{b.warentyp}{b.warenbezeichnung ? ` – ${b.warenbezeichnung}` : ""}</span>
        </div>
        <div className="buchung-actions" style={{ display: "flex", gap: 6 }}>
          {b.vorgang === "Ausgang" && b.verfuegbar > 0 && (
            <button style={{ ...s.btnSmall, borderColor: "#2196c4", color: "#2196c4" }} onClick={() => setLieferscheinModal({ buchung: b, uebergabeAn: "" })}>
              ✉ Lieferschein{b.lieferschein_gesendet_am ? ` (${new Date(b.lieferschein_gesendet_am).toLocaleDateString("de-DE")})` : ""}
            </button>
          )}
          {istRuecknahme(b)
            ? null
            : b.vorgang === "Eingang"
              ? <button style={s.btnSmall} onClick={() => handleAusbuchen(b.id, b.verfuegbar ?? b.menge)}>→ Ausbuchen</button>
              : b.verfuegbar === 0
                ? <span style={{ fontSize: 11, color: "#2d7a11", fontWeight: 600, padding: "4px 8px", background: "#e8f5e0", borderRadius: 6 }}>✓ Zurückgenommen</span>
                : <button style={s.btnSmall} onClick={() => handleAusbuchen(b.id, b.verfuegbar ?? b.menge)}>→ Einbuchen</button>
          }
          {isAdmin && !istRuecknahme(b) && <button style={{ ...s.btnSmall, borderColor: "#8a9bb0", color: "#8a9bb0" }} onClick={() => setEditModal({ ...b })}>✎ Bearbeiten</button>}
          {isAdmin && <button style={s.btnDanger} onClick={() => handleDelete(b.id)}>✕</button>}
        </div>
      </div>
      <div style={{ fontSize: 13, color: "#555" }}>
        {b.kunde}{b.kundennummer ? ` (${b.kundennummer})` : ""}
        {b.auftragsnummer ? ` · Auftrag: ${b.auftragsnummer}` : ""}
        {" · "}<span style={{ fontWeight: 600 }}>{b.menge} Stk</span>
        {b.vorgang === "Eingang" && !istRuecknahme(b) && b.verfuegbar !== undefined && b.menge !== b.verfuegbar
          ? <span style={{ color: BLUE }}> ({b.verfuegbar} verfügbar)</span>
          : null
        }
        {b.vorgang === "Ausgang" && b.verfuegbar > 0 && b.verfuegbar < b.menge
          ? <span style={{ color: "#e67e22", fontWeight: 600 }}> · {b.menge - b.verfuegbar} von {b.menge} Stk zurückgenommen</span>
          : null
        }
      </div>
      {b.seriennummer && <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>SN: {b.seriennummer}</div>}
      {b.bemerkung && <div style={{ fontSize: 12, color: "#777", marginTop: 2, fontStyle: "italic" }}>{b.bemerkung}</div>}
      <div style={{ fontSize: 11, color: "#aaa", marginTop: 4 }}>{new Date(b.datum).toLocaleString("de-DE")} · {b.bearbeiter_name}</div>
    </div>
  );

  if (!isAuthenticated) return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "#0f1923" }}>
      <style>{`@keyframes fadeIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}`}</style>
      <div style={{ background: "#fff", borderRadius: 16, padding: "40px 36px", width: "100%", maxWidth: 360, textAlign: "center", animation: "fadeIn 0.4s ease", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
          <svg width="48" height="48" viewBox="0 0 100 100" fill="none">
            <polygon points="50,5 95,90 5,90" fill="none" stroke="#0f1923" strokeWidth="10" />
            <polygon points="50,40 70,75 30,75" fill={BLUE} />
          </svg>
        </div>
        <div style={{ fontSize: 22, fontWeight: 700, marginBottom: 4 }}>ARTAKER <span style={{ color: BLUE }}>IT</span></div>
        <div style={{ fontSize: 13, color: "#888", marginBottom: 28 }}>Lagerverwaltung</div>
        <button style={{ ...s.btnPrimary, width: "100%", padding: "12px" }} onClick={login}>Mit Microsoft anmelden</button>
      </div>
    </div>
  );

  const eingang = buchungen.filter(b => b.vorgang === "Eingang").length;
  const ausgang = buchungen.filter(b => b.vorgang === "Ausgang").length;
  const gesamt = buchungen.length;
  const navItems = [...NAV, ...(isAdmin ? [NAV_ADMIN] : [])];

  return (
    <div style={s.wrap}>
      <style>{`
        @keyframes fadeIn{from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        * { box-sizing: border-box; }
        input:focus, select:focus, textarea:focus { border-color: ${BLUE} !important; outline: none; box-shadow: 0 0 0 3px rgba(33,150,196,0.12); }
        button:hover { opacity: 0.88; }
        @media(max-width:640px){
          .sidebar { transform: translateX(-100%); }
          .sidebar.open { transform: translateX(0); }
          .main { margin-left: 0 !important; }
          .hamburger { display: block !important; }
          .metrics { grid-template-columns: 1fr 1fr !important; }
          .content { padding: 12px !important; }
          .buchung-actions { flex-wrap: wrap; gap: 4px !important; }
          .buchung-actions button { font-size: 11px !important; padding: 4px 8px !important; }
        }
      `}</style>

      {sidebarOpen && <div style={{ display: "block", position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 99 }} onClick={() => setSidebarOpen(false)} />}

      <div className={`sidebar${sidebarOpen ? " open" : ""}`} style={s.sidebar}>
        <div style={s.sidebarLogo}>
          <svg width="28" height="28" viewBox="0 0 100 100" fill="none">
            <polygon points="50,5 95,90 5,90" fill="none" stroke="white" strokeWidth="10" />
            <polygon points="50,40 70,75 30,75" fill={BLUE} />
          </svg>
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#fff", letterSpacing: 1 }}>ARTAKER <span style={{ color: BLUE }}>IT</span></div>
            <div style={{ fontSize: 11, color: "#8a9bb0" }}>Lagerverwaltung</div>
          </div>
        </div>
        <nav style={{ flex: 1, paddingTop: 8 }}>
          {navItems.map(item => (
            <div key={item.id} style={s.navItem(screen === item.id)} onClick={() => { navigate(item.id); if (item.id === "lager") loadLagerbestand(lagerKunde); }}>
              <span style={s.navIcon}>{item.icon}</span>
              <span>{item.label}</span>
              {item.id === "kleinteile" && kleinteile.length > 0 && <span style={{ ...s.badge, marginLeft: "auto", fontSize: 10 }}>{kleinteile.length}</span>}
            </div>
          ))}
        </nav>
        <div style={{ padding: "16px 20px", borderTop: "1px solid #1e2d3d" }}>
          <div style={{ fontSize: 13, color: "#fff", fontWeight: 500, marginBottom: 2 }}>{user?.name}</div>
          {isAdmin && <span style={s.adminBadge}>Admin</span>}
          <div style={{ marginTop: 10 }}>
            <button style={{ ...s.btnGhost, padding: "6px 0", fontSize: 12 }} onClick={logout}>↪ Abmelden</button>
          </div>
        </div>
      </div>

      <div className="main" style={{ ...s.main, marginLeft: SIDEBAR_W }}>
        <div style={s.topbar}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button className="hamburger" style={s.hamburger} onClick={() => setSidebarOpen(o => !o)}>☰</button>
            <span style={{ fontWeight: 600, fontSize: 16 }}>{navItems.find(n => n.id === screen)?.label || ""}</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: "50%", background: BLUE, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700 }}>{initials(user?.name)}</div>
            <span style={{ fontSize: 13, color: "#555" }}>{user?.name}</span>
          </div>
        </div>

        <div className="content" style={s.content}>
          {error && <div style={s.errorBox}><span>{error}</span><button style={s.btnSmall} onClick={() => setError(null)}>✕</button></div>}

          {/* Lieferschein Modal */}
          {lieferscheinModal && (
            <div style={s.overlay} onClick={e => e.target === e.currentTarget && setLieferscheinModal(null)}>
              <div style={s.modal}>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>Lieferschein senden</div>
                <div style={{ fontSize: 13, color: "#888", marginBottom: 20 }}>
                  {lieferscheinModal.buchung.warentyp}{lieferscheinModal.buchung.warenbezeichnung ? ` – ${lieferscheinModal.buchung.warenbezeichnung}` : ""} → {lieferscheinModal.buchung.kunde}
                </div>
                <div style={s.row1}>
                  <label style={s.label}>Übergeben an <span style={{ color: "#aaa", fontWeight: 400, textTransform: "none" }}>(optional)</span></label>
                  <input value={lieferscheinModal.uebergabeAn || ""} onChange={e => setLieferscheinModal(m => ({ ...m, uebergabeAn: e.target.value }))} placeholder="z.B. Max Mustermann" style={s.input} autoFocus />
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                  <button style={s.btnPrimary} onClick={async () => {
                    try {
                      const res = await apiFetch(`/buchungen/${lieferscheinModal.buchung.id}/lieferschein`, { method: "POST", body: JSON.stringify({ uebergabeAn: lieferscheinModal.uebergabeAn || "" }) });
                      const d = await res.json();
                      setLieferscheinModal(null);
                      alert(`✅ Lieferschein gesendet an ${d.sentTo}`);
                      await loadBuchungen(filterKunde, filterSuche);
                    } catch(e) { setError(e.message); }
                  }}>✉ Senden</button>
                  <button style={s.btn} onClick={() => setLieferscheinModal(null)}>Abbrechen</button>
                </div>
              </div>
            </div>
          )}

          {/* Bearbeiten Modal */}
          {editModal && (
            <div style={s.overlay} onClick={e => e.target === e.currentTarget && setEditModal(null)}>
              <div style={{ ...s.modal, maxWidth: 560 }}>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 16 }}>Buchung bearbeiten</div>
                <div style={s.row2}>
                  <div>
                    <label style={s.label}>Warentyp *</label>
                    <select value={editModal.warentyp} onChange={e => setEditModal(m => ({ ...m, warentyp: e.target.value }))} style={s.input}>
                      {warentypen.map(w => <option key={w.id} value={w.name}>{w.name}</option>)}
                    </select>
                  </div>
                  <div>
                    <label style={s.label}>Warenbezeichnung</label>
                    <input value={editModal.warenbezeichnung || ""} onChange={e => setEditModal(m => ({ ...m, warenbezeichnung: e.target.value }))} style={s.input} />
                  </div>
                </div>
                <div style={s.row2}>
                  <div>
                    <label style={s.label}>Kunde *</label>
                    <KundenCombobox kunden={kunden} value={editModal.kunde} onChange={k => setEditModal(m => ({ ...m, kunde: k.name, kundennummer: k.kundennummer || m.kundennummer }))} />
                  </div>
                  <div>
                    <label style={s.label}>Kundennummer</label>
                    <input value={editModal.kundennummer || ""} onChange={e => setEditModal(m => ({ ...m, kundennummer: e.target.value }))} style={s.input} />
                  </div>
                </div>
                <div style={s.row2}>
                  <div>
                    <label style={s.label}>Seriennummer</label>
                    <input value={editModal.seriennummer || ""} onChange={e => setEditModal(m => ({ ...m, seriennummer: e.target.value }))} style={s.input} />
                  </div>
                  <div>
                    <label style={s.label}>Menge</label>
                    <input type="number" min="1" value={editModal.menge} onChange={e => setEditModal(m => ({ ...m, menge: e.target.value }))} style={s.input} />
                  </div>
                </div>
                <div style={s.row1}>
                  <label style={s.label}>Auftragsnummer</label>
                  <input value={editModal.auftragsnummer || ""} onChange={e => setEditModal(m => ({ ...m, auftragsnummer: e.target.value }))} style={s.input} />
                </div>
                <div style={s.row1}>
                  <label style={s.label}>Bemerkung</label>
                  <textarea value={editModal.bemerkung || ""} onChange={e => setEditModal(m => ({ ...m, bemerkung: e.target.value }))} rows={2} style={{ ...s.input, resize: "vertical" }} />
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                  <button style={s.btnPrimary} onClick={async () => {
                    try {
                      await apiFetch(`/buchungen/${editModal.id}`, { method: "PUT", body: JSON.stringify(editModal) });
                      setEditModal(null);
                      await loadBuchungen(filterKunde, filterSuche);
                      await loadLagerbestand(lagerKunde);
                      await loadEditLog();
                    } catch(e) { setError(e.message); }
                  }}>Speichern</button>
                  <button style={s.btn} onClick={() => setEditModal(null)}>Abbrechen</button>
                </div>
              </div>
            </div>
          )}

          {/* Sammel-Ausbuchen Modal */}
          {sammelModal && (
            <div style={s.overlay} onClick={e => e.target === e.currentTarget && setSammelModal(null)}>
              <div style={{ ...s.modal, maxWidth: 560 }}>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>📦 Mehrere ausbuchen</div>
                <div style={{ fontSize: 13, color: "#888", marginBottom: 16 }}>{sammelModal.warentyp}{sammelModal.warenbezeichnung ? ` – ${sammelModal.warenbezeichnung}` : ""}</div>
                <div style={s.row2}>
                  <div>
                    <label style={s.label}>Kunde *</label>
                    <KundenCombobox kunden={kunden} value={sammelForm.kunde} onChange={k => setSammelForm(f => ({ ...f, kunde: k.name, kundennummer: k.kundennummer || "" }))} />
                  </div>
                  <div>
                    <label style={s.label}>Auftragsnummer</label>
                    <input value={sammelForm.auftragsnummer} onChange={e => setSammelForm(f => ({ ...f, auftragsnummer: e.target.value }))} placeholder="A-2024-001" style={s.input} />
                  </div>
                </div>
                <div style={s.row1}>
                  <label style={s.label}>Bemerkung</label>
                  <input value={sammelForm.bemerkung} onChange={e => setSammelForm(f => ({ ...f, bemerkung: e.target.value }))} placeholder="Optional…" style={s.input} />
                </div>
                <div style={{ marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <label style={s.label}>Seriennummern auswählen ({sammelAuswahl.length} / {sammelModal.items.length})</label>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button style={s.btnSmall} onClick={() => setSammelAuswahl(sammelModal.items.map(i => i.id))}>Alle</button>
                    <button style={s.btnSmall} onClick={() => setSammelAuswahl([])}>Keine</button>
                  </div>
                </div>
                <div style={{ maxHeight: 260, overflowY: "auto", border: "1px solid #eef0f3", borderRadius: 8, marginBottom: 16 }}>
                  {sammelModal.items.map(item => (
                    <div key={item.id}
                      onClick={() => setSammelAuswahl(a => a.includes(item.id) ? a.filter(x => x !== item.id) : [...a, item.id])}
                      style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderBottom: "0.5px solid #f0f2f5", cursor: "pointer", background: sammelAuswahl.includes(item.id) ? "rgba(33,150,196,0.06)" : "#fff" }}>
                      <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${sammelAuswahl.includes(item.id) ? BLUE : "#dde1e7"}`, background: sammelAuswahl.includes(item.id) ? BLUE : "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                        {sammelAuswahl.includes(item.id) && <span style={{ color: "#fff", fontSize: 12, lineHeight: 1 }}>✓</span>}
                      </div>
                      <span style={{ fontSize: 13, fontFamily: "monospace" }}>{item.seriennummer || "—"}</span>
                      <span style={{ fontSize: 12, color: "#888", marginLeft: "auto" }}>{item.kunde}</span>
                    </div>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 10 }}>
                  <button style={s.btnPrimary} disabled={sammelAuswahl.length === 0 || !sammelForm.kunde} onClick={async () => {
                    try {
                      await apiFetch("/buchungen/sammelausbuchen", {
                        method: "POST",
                        body: JSON.stringify({ ids: sammelAuswahl, kunde: sammelForm.kunde, kundennummer: sammelForm.kundennummer, auftragsnummer: sammelForm.auftragsnummer, bemerkung: sammelForm.bemerkung })
                      });
                      setSammelModal(null);
                      setSammelAuswahl([]);
                      setSammelForm({ kunde: "", kundennummer: "", auftragsnummer: "", bemerkung: "" });
                      await loadLagerbestand(lagerKunde);
                      await loadBuchungen(filterKunde, filterSuche);
                      alert(`✅ ${sammelAuswahl.length} Geräte ausgebucht`);
                    } catch(e) { setError(e.message); }
                  }}>→ {sammelAuswahl.length} Stk ausbuchen</button>
                  <button style={s.btn} onClick={() => { setSammelModal(null); setSammelAuswahl([]); }}>Abbrechen</button>
                </div>
              </div>
            </div>
          )}

          {/* Kleinteil Modal */}
          {ausbuchenModal && (
            <div style={s.overlay} onClick={e => e.target === e.currentTarget && setAusbuchenModal(null)}>
              <div style={s.modal}>
                <div style={{ fontWeight: 700, fontSize: 18, marginBottom: 4 }}>Kleinteil ausbuchen</div>
                <div style={{ fontSize: 13, color: "#888", marginBottom: 20 }}>{ausbuchenModal.kleinteil.name}</div>
                <div style={s.row2}>
                  <div>
                    <label style={s.label}>Kunde *</label>
                    <KundenCombobox kunden={kunden} value={ausbuchenForm.kunde} onChange={k => setAusbuchenForm(f => ({ ...f, kunde: k.name, kundennummer: k.kundennummer || "" }))} />
                  </div>
                  <div>
                    <label style={s.label}>Menge *</label>
                    <input type="number" min="1" value={ausbuchenForm.menge} onChange={e => setAusbuchenForm(f => ({ ...f, menge: e.target.value }))} style={s.input} />
                  </div>
                </div>
                <div style={s.row1}><label style={s.label}>Auftragsnummer</label><input value={ausbuchenForm.auftragsnummer} onChange={e => setAusbuchenForm(f => ({ ...f, auftragsnummer: e.target.value }))} placeholder="A-2024-001" style={s.input} /></div>
                <div style={s.row1}><label style={s.label}>Bemerkung</label><textarea value={ausbuchenForm.bemerkung} onChange={e => setAusbuchenForm(f => ({ ...f, bemerkung: e.target.value }))} rows={2} style={{ ...s.input, resize: "vertical" }} /></div>
                <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                  <button style={s.btnPrimary} disabled={!ausbuchenForm.kunde || !ausbuchenForm.menge} onClick={async () => {
                    try {
                      await apiFetch(`/kleinteile/${ausbuchenModal.kleinteil.id}/ausbuchen`, { method: "POST", body: JSON.stringify(ausbuchenForm) });
                      setAusbuchenModal(null); setAusbuchenForm({ kunde: "", kundennummer: "", auftragsnummer: "", menge: 1, bemerkung: "" });
                      await loadKleinteilAusbuchungen();
                    } catch(e) { setError(e.message); }
                  }}>Ausbuchen</button>
                  <button style={s.btn} onClick={() => setAusbuchenModal(null)}>Abbrechen</button>
                </div>
              </div>
            </div>
          )}

          {/* ── DASHBOARD ── */}
          {screen === "dashboard" && (
            <div style={{ animation: "fadeIn 0.2s ease" }}>
              {submitted && <div style={s.success}>✓ Buchung erfolgreich gespeichert.</div>}
              <div className="metrics" style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 24 }}>
                <div style={s.metric}><div style={s.metricVal}>{gesamt}</div><div style={s.metricLbl}>Buchungen gesamt</div></div>
                <div style={{ ...s.metric, background: "linear-gradient(135deg, #1a3a1a 0%, #0f1923 100%)" }}><div style={{ ...s.metricVal, color: "#5cb85c" }}>{eingang}</div><div style={s.metricLbl}>Eingang</div></div>
                <div style={{ ...s.metric, background: "linear-gradient(135deg, #3a1a1a 0%, #0f1923 100%)" }}><div style={{ ...s.metricVal, color: "#e74c3c" }}>{ausgang}</div><div style={s.metricLbl}>Ausgang</div></div>
              </div>
              {lagerbestand.length > 0 && (() => {
                const grouped = lagerbestand.reduce((acc, i) => { acc[i.warentyp] = (acc[i.warentyp] || 0) + i.bestand; return acc; }, {});
                return (
                  <div style={{ ...s.card, marginBottom: 20 }}>
                    <div style={s.cardTitle}>Lagerbestand Übersicht</div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 10 }}>
                      {Object.entries(grouped).map(([typ, menge]) => (
                        <div key={typ} onClick={() => { setLagerTypFilter(typ); navigate("lager"); loadLagerbestand(""); }}
                          style={{ background: "#f8fafc", borderRadius: 10, padding: "12px", textAlign: "center", cursor: "pointer", border: "1px solid #eef0f3", transition: "all 0.15s" }}
                          onMouseEnter={e => { e.currentTarget.style.borderColor = BLUE; e.currentTarget.style.background = "rgba(33,150,196,0.04)"; }}
                          onMouseLeave={e => { e.currentTarget.style.borderColor = "#eef0f3"; e.currentTarget.style.background = "#f8fafc"; }}>
                          <div style={{ fontSize: 22, fontWeight: 700, color: BLUE }}>{menge}</div>
                          <div style={{ fontSize: 11, color: "#666", marginTop: 2 }}>{typ}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
              {filterBar}
              {loading && <div style={{ textAlign: "center", color: "#888", padding: 30 }}>Lädt…</div>}
              {!loading && buchungen.length === 0 && <div style={{ ...s.card, textAlign: "center", color: "#888", padding: "40px 20px" }}>Keine Buchungen gefunden.</div>}
              {buchungen.slice(0, 10).map(b => <BuchungCard key={b.id} b={b} />)}
              {buchungen.length > 10 && <div style={{ textAlign: "center", fontSize: 13, color: "#888", padding: "8px 0" }}>+ {buchungen.length - 10} weitere → <span style={{ color: BLUE, cursor: "pointer" }} onClick={() => navigate("historie")}>alle in der Historie</span></div>}
            </div>
          )}

          {/* ── NEUE BUCHUNG ── */}
          {screen === "buchen" && (
            <div style={{ animation: "fadeIn 0.2s ease" }}>
              <div style={s.card}>
                <div style={s.cardTitle}>Eingang buchen</div>
                <div style={s.fotoBox} onClick={() => fileRef.current.click()}>
                  {fotoPreview ? <img src={fotoPreview} style={{ maxHeight: 120, borderRadius: 8 }} alt="" />
                    : <div style={{ color: "#888", fontSize: 13 }}>📷 Foto aufnehmen oder Datei wählen<br /><span style={{ fontSize: 11, color: "#aaa" }}>KI erkennt Gerät und füllt Felder automatisch</span></div>}
                  {scanning && <div style={{ marginTop: 8, color: BLUE, fontSize: 13 }}>⟳ KI analysiert…</div>}
                  {scanResult && !scanResult.error && <div style={{ marginTop: 8, color: "#2d7a11", fontSize: 12 }}>✓ Felder automatisch befüllt</div>}
                  {scanResult?.error && <div style={{ marginTop: 8, color: "#c0392b", fontSize: 12 }}>✕ {scanResult.error}</div>}
                </div>
                <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={handleFoto} />
                <div style={s.row2}>
                  <div>
                    <label style={s.label}>Kundenname *</label>
                    <KundenCombobox kunden={kunden} value={form.kunde} onChange={k => setForm(f => ({ ...f, kunde: k.name, kundennummer: k.kundennummer || f.kundennummer }))} />
                  </div>
                  <div><label style={s.label}>Kundennummer</label><input value={form.kundennummer} onChange={e => setForm(f => ({ ...f, kundennummer: e.target.value }))} placeholder="K-12345" style={s.input} /></div>
                </div>
                <div style={s.row1}><label style={s.label}>Auftragsnummer</label><input value={form.auftragsnummer} onChange={e => setForm(f => ({ ...f, auftragsnummer: e.target.value }))} placeholder="A-2024-001" style={s.input} /></div>
                <div style={s.row2}>
                  <div>
                    <label style={s.label}>Warentyp *</label>
                    <select value={form.warentyp} onChange={e => setForm(f => ({ ...f, warentyp: e.target.value }))} style={s.input}>
                      <option value="">Bitte wählen…</option>
                      {warentypen.map(w => <option key={w.id} value={w.name}>{w.name}</option>)}
                    </select>
                  </div>
                  <div><label style={s.label}>Warenbezeichnung</label><input value={form.warenbezeichnung} onChange={e => setForm(f => ({ ...f, warenbezeichnung: e.target.value }))} placeholder="z.B. Dell Latitude 5540" style={s.input} /></div>
                </div>
                <div style={s.row2}>
                  <div><label style={s.label}>Seriennummer</label><input value={form.seriennummer} onChange={e => setForm(f => ({ ...f, seriennummer: e.target.value }))} placeholder="SN-XXXXXXXX" style={s.input} autoFocus={schnellErfassung} /></div>
                  {!schnellErfassung && <div><label style={s.label}>Menge</label><input type="number" min="1" value={form.menge} onChange={e => setForm(f => ({ ...f, menge: e.target.value }))} style={s.input} /></div>}
                </div>
                <div style={s.row1}><label style={s.label}>Bemerkung</label><textarea value={form.bemerkung} onChange={e => setForm(f => ({ ...f, bemerkung: e.target.value }))} rows={3} placeholder="Zustand, Besonderheiten…" style={{ ...s.input, resize: "vertical" }} /></div>
                <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap" }}>
                  {!schnellErfassung
                    ? <>
                        <button style={s.btnPrimary} onClick={handleSubmit} disabled={!form.kunde || !form.warentyp}>Buchung speichern</button>
                        <button style={{ ...s.btnPrimary, background: "#2d7a11" }} onClick={() => { setSchnellErfassung(true); setForm(f => ({ ...f, menge: 1, seriennummer: "" })); }} disabled={!form.kunde || !form.warentyp}>⚡ Schnell-Erfassung starten</button>
                        <button style={s.btn} onClick={() => { setForm(initialForm); setFotoPreview(null); setScanResult(null); navigate("dashboard"); }}>Abbrechen</button>
                      </>
                    : <>
                        <button style={{ ...s.btnPrimary, background: "#2d7a11" }} onClick={async () => {
                          if (!form.kunde || !form.warentyp) return;
                          try {
                            await apiFetch("/buchungen", { method: "POST", body: JSON.stringify({ ...form, menge: 1 }) });
                            setForm(f => ({ ...f, seriennummer: "" }));
                            setFotoPreview(null); setScanResult(null);
                            setSubmitted(true); setTimeout(() => setSubmitted(false), 1500);
                            await loadBuchungen(filterKunde, filterSuche); await loadLagerbestand(lagerKunde);
                          } catch(e) { setError(e.message); }
                        }} disabled={!form.kunde || !form.warentyp}>⚡ Speichern & weiter</button>
                        <button style={s.btn} onClick={() => { setSchnellErfassung(false); setForm(initialForm); setFotoPreview(null); setScanResult(null); navigate("dashboard"); }}>✓ Fertig</button>
                      </>
                  }
                </div>
                {schnellErfassung && (
                  <div style={{ marginTop: 10, padding: "8px 14px", background: "#e8f5e0", borderRadius: 8, fontSize: 13, color: "#2d7a11" }}>
                    ⚡ Schnell-Erfassung aktiv — Menge ist auf 1 fixiert. Nur Seriennummer ändern und speichern.
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── LAGER ── */}
          {screen === "lager" && (
            <div style={{ animation: "fadeIn 0.2s ease" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
                <select value={lagerKunde} onChange={e => { setLagerKunde(e.target.value); loadLagerbestand(e.target.value); }} style={{ ...s.input, width: "auto", minWidth: 160 }}>
                  <option value="">Alle Kunden</option>
                  {kunden.map(k => <option key={k.id} value={k.name}>{k.name}</option>)}
                </select>
                <select value={lagerTypFilter} onChange={e => setLagerTypFilter(e.target.value)} style={{ ...s.input, width: "auto", minWidth: 140 }}>
                  <option value="">Alle Warentypen</option>
                  {warentypen.map(w => <option key={w.id} value={w.name}>{w.name}</option>)}
                </select>
                {lagerTypFilter && <button style={s.btn} onClick={() => setLagerTypFilter("")}>✕ {lagerTypFilter}</button>}
              </div>
              {lagerbestand.length === 0 && <div style={{ ...s.card, textAlign: "center", color: "#888", padding: "40px 20px" }}>Kein Lagerbestand vorhanden.</div>}
              {(() => {
                const grouped = lagerbestand
                  .filter(item => !lagerTypFilter || item.warentyp === lagerTypFilter)
                  .reduce((acc, item) => { if (!acc[item.warentyp]) acc[item.warentyp] = []; acc[item.warentyp].push(item); return acc; }, {});
                return Object.entries(grouped).map(([typ, items]) => (
                  <div key={typ} style={s.card}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                      <span style={{ fontWeight: 600, fontSize: 15 }}>{typ}</span>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        {items.length > 1 && (
                          <button style={{ ...s.btnSmall, borderColor: BLUE, color: BLUE }} onClick={() => {
                            setSammelModal({ items, warentyp: typ, warenbezeichnung: items[0]?.warenbezeichnung || "" });
                            setSammelAuswahl([]);
                            setSammelForm({ kunde: items[0]?.kunde || "", kundennummer: items[0]?.kundennummer || "", auftragsnummer: "", bemerkung: "" });
                          }}>📦 Mehrere ausbuchen</button>
                        )}
                        <span style={s.badge}>{items.reduce((s, i) => s + i.bestand, 0)} gesamt</span>
                      </div>
                    </div>
                    {items.map((item, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderTop: "1px solid #f0f2f5" }}>
                        <div>
                          <span style={{ fontSize: 14, fontWeight: 500 }}>{item.warenbezeichnung || "—"}</span>
                          {!lagerKunde && <span style={{ fontSize: 12, color: "#888", marginLeft: 8 }}>{item.kunde}</span>}
                          {item.seriennummer && <span style={{ fontSize: 12, color: "#aaa", marginLeft: 8 }}>SN: {item.seriennummer}</span>}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                          <span style={{ fontWeight: 700, color: BLUE, fontSize: 15 }}>{item.bestand} Stk</span>
                          <button style={s.btnSmall} onClick={async () => {
                            let menge = item.bestand;
                            if (item.bestand > 1) {
                              const input = window.prompt(`Wie viele Stück ausbuchen? (max. ${item.bestand})`, item.bestand);
                              if (!input) return; menge = parseInt(input);
                              if (isNaN(menge) || menge < 1 || menge > item.bestand) { setError(`Ungültige Menge.`); return; }
                            } else { if (!window.confirm("Gegenbuchung erstellen?")) return; }
                            try {
                              const ids = item.ids || [{ id: item.id, bestand: item.bestand }];
                              let rest = menge;
                              for (const entry of ids) {
                                if (rest <= 0) break;
                                const zuBuchen = Math.min(rest, entry.bestand);
                                await apiFetch(`/buchungen/${entry.id}/ausbuchen`, { method: "POST", body: JSON.stringify({ menge: zuBuchen }) });
                                rest -= zuBuchen;
                              }
                              await loadLagerbestand(lagerKunde); await loadBuchungen(filterKunde, filterSuche);
                            } catch(e) { setError(e.message); }
                          }}>→ Ausbuchen</button>
                        </div>
                      </div>
                    ))}
                  </div>
                ));
              })()}
            </div>
          )}

          {/* ── HISTORIE ── */}
          {screen === "historie" && (
            <div style={{ animation: "fadeIn 0.2s ease" }}>
              {filterBar}
              {buchungen.map(b => <BuchungCard key={b.id} b={b} />)}
            </div>
          )}

          {/* ── KLEINTEILE ── */}
          {screen === "kleinteile" && (
            <div style={{ animation: "fadeIn 0.2s ease" }}>
              <div style={s.card}>
                <div style={s.cardTitle}>Verfügbare Artikel</div>
                {kleinteile.length === 0 && <div style={{ color: "#888", fontSize: 14 }}>Noch keine Kleinteile angelegt.</div>}
                {kleinteile.map(k => (
                  <div key={k.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 0", borderTop: "1px solid #f0f2f5" }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{k.name}</div>
                      <div style={{ fontSize: 12, color: "#aaa" }}>unbegrenzter Lagerstand</div>
                    </div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <button style={s.btnOrange} onClick={() => { setAusbuchenModal({ kleinteil: k }); setAusbuchenForm({ kunde: "", kundennummer: "", auftragsnummer: "", menge: 1, bemerkung: "" }); }}>→ Ausbuchen</button>
                      {isAdmin && <button style={s.btnDanger} onClick={async () => { if (!window.confirm("Kleinteil löschen?")) return; try { await apiFetch(`/kleinteile/${k.id}`, { method: "DELETE" }); await loadKleinteile(); } catch(e) { setError(e.message); } }}>✕</button>}
                    </div>
                  </div>
                ))}
                {isAdmin && (
                  <div style={{ marginTop: 16, display: "flex", gap: 8 }}>
                    <input value={neuesKleinteil} onChange={e => setNeuesKleinteil(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter" && neuesKleinteil.trim()) apiFetch("/kleinteile", { method: "POST", body: JSON.stringify({ name: neuesKleinteil.trim() }) }).then(() => { setNeuesKleinteil(""); loadKleinteile(); }).catch(e => setError(e.message)); }}
                      placeholder="Neuer Artikel, z.B. Netzwerkkabel Cat6…" style={{ ...s.input, flex: 1 }} />
                    <button style={s.btnPrimary} disabled={!neuesKleinteil.trim()} onClick={async () => { try { await apiFetch("/kleinteile", { method: "POST", body: JSON.stringify({ name: neuesKleinteil.trim() }) }); setNeuesKleinteil(""); await loadKleinteile(); } catch(e) { setError(e.message); } }}>+ Anlegen</button>
                  </div>
                )}
              </div>
              <div style={s.card}>
                <div style={s.cardTitle}>Ausbuchungshistorie</div>
                {kleinteilAusbuchungen.length === 0 && <div style={{ color: "#888", fontSize: 14 }}>Noch keine Ausbuchungen.</div>}
                {kleinteilAusbuchungen.map(a => (
                  <div key={a.id} style={{ padding: "10px 0", borderTop: "1px solid #f0f2f5" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{a.kleinteil_name}</span>
                        <span style={{ fontSize: 13, color: "#555", marginLeft: 10 }}>{a.menge} Stk → {a.kunde}</span>
                        {a.auftragsnummer && <span style={{ fontSize: 12, color: "#888", marginLeft: 8 }}>· {a.auftragsnummer}</span>}
                      </div>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span style={{ fontSize: 11, color: "#aaa" }}>{new Date(a.datum).toLocaleString("de-DE")}</span>
                        {isAdmin && <button style={s.btnDanger} onClick={async () => {
                          if (!window.confirm("Ausbuchung löschen?")) return;
                          try { await apiFetch(`/kleinteil-ausbuchungen/${a.id}`, { method: "DELETE" }); await loadKleinteilAusbuchungen(); } catch(e) { setError(e.message); }
                        }}>✕</button>}
                      </div>
                    </div>
                    {a.bemerkung && <div style={{ fontSize: 12, color: "#777", marginTop: 2, fontStyle: "italic" }}>{a.bemerkung}</div>}
                    <div style={{ fontSize: 11, color: "#bbb", marginTop: 2 }}>{a.bearbeiter_name}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── ADMIN ── */}
          {screen === "admin" && isAdmin && (
            <div style={{ animation: "fadeIn 0.2s ease" }}>

              <div style={s.card}>
                <div style={s.cardTitle}>API Key — Externe Integration (z.B. SAP B1)</div>
                <div style={{ fontSize: 13, color: "#666", marginBottom: 14 }}>Mit diesem Key kann ein externes System Kunden importieren ohne Azure AD Login.</div>
                {apiKey ? (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      <code style={{ flex: 1, background: "#f0f2f5", padding: "8px 12px", borderRadius: 8, fontSize: 12, fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {apiKeyVisible ? apiKey : "•".repeat(32)}
                      </code>
                      <button style={s.btnSmall} onClick={() => setApiKeyVisible(v => !v)}>{apiKeyVisible ? "Verbergen" : "Anzeigen"}</button>
                      <button style={s.btnSmall} onClick={() => { navigator.clipboard.writeText(apiKey); alert("✅ Key kopiert!"); }}>Kopieren</button>
                    </div>
                    <div style={{ background: "#f8fafc", borderRadius: 8, padding: "12px 14px", fontSize: 12, fontFamily: "monospace", color: "#555", marginBottom: 12, lineHeight: 1.8 }}>
                      POST https://warenlogistik-api.azurewebsites.net/api/kunden/import<br/>
                      x-api-key: {apiKeyVisible ? apiKey : "••••••••••••••••"}<br/>
                      Content-Type: application/json<br/><br/>
                      {`{ "kunden": [{ "name": "Mustermann GmbH", "kundennummer": "K-001" }] }`}
                    </div>
                    <button style={s.btnDanger} onClick={async () => {
                      if (!window.confirm("Neuen API Key generieren? Der alte Key wird ungültig!")) return;
                      try { const d = await (await apiFetch("/kunden/apikey/generate", { method: "POST" })).json(); setApiKey(d.apiKey); setApiKeyVisible(true); } catch(e) { setError(e.message); }
                    }}>⟳ Neuen Key generieren</button>
                  </div>
                ) : (
                  <button style={s.btnPrimary} onClick={async () => {
                    try { const d = await (await apiFetch("/kunden/apikey/generate", { method: "POST" })).json(); setApiKey(d.apiKey); setApiKeyVisible(true); } catch(e) { setError(e.message); }
                  }}>API Key generieren</button>
                )}
              </div>

              <div style={s.card}>
                <div style={s.cardTitle}>Export</div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 500 }}>Lager-Export CSV</div>
                    <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>Alle Buchungen & Kleinteile als CSV-Datei</div>
                  </div>
                  <button style={s.btnPrimary} onClick={exportCSV}>⬇ Download CSV</button>
                </div>
              </div>

              <div style={s.card}>
                <div style={s.cardTitle}>Kunden importieren</div>
                <div style={{ fontSize: 13, color: "#666", marginBottom: 12 }}>
                  CSV oder Excel (.xlsx) — Spalten: <code style={{ fontSize: 12, background: "#f0f2f5", padding: "1px 6px", borderRadius: 4 }}>Name</code> und <code style={{ fontSize: 12, background: "#f0f2f5", padding: "1px 6px", borderRadius: 4 }}>Kundennummer</code>
                </div>
                <div style={{ border: "2px dashed #dde1e7", borderRadius: 10, padding: "24px", textAlign: "center", cursor: "pointer", background: "#fafbfc" }}
                  onClick={() => importRef.current.click()}
                  onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = BLUE; }}
                  onDragLeave={e => { e.currentTarget.style.borderColor = "#dde1e7"; }}
                  onDrop={e => { e.preventDefault(); e.currentTarget.style.borderColor = "#dde1e7"; handleImport(e.dataTransfer.files[0]); }}>
                  {importLoading
                    ? <div style={{ color: BLUE, fontSize: 13 }}>⟳ Importiere…</div>
                    : <div style={{ color: "#888", fontSize: 13 }}>📂 Datei hier ablegen oder klicken<br /><span style={{ fontSize: 11, color: "#aaa" }}>CSV oder Excel (.xlsx)</span></div>}
                </div>
                <input ref={importRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: "none" }} onChange={e => handleImport(e.target.files[0])} />
                {importResult && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ background: "#e8f5e0", color: "#2d7a11", borderRadius: 8, padding: "10px 14px", fontSize: 13, marginBottom: importResult.skippedList?.length > 0 ? 8 : 0 }}>
                      ✓ {importResult.imported} Kunden erfolgreich importiert
                    </div>
                    {importResult.skippedList?.length > 0 && (
                      <div style={{ background: "#fff3cd", color: "#856404", borderRadius: 8, padding: "10px 14px", fontSize: 13 }}>
                        ⚠ {importResult.skipped} bereits vorhanden: {importResult.skippedList.join(", ")}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={s.card}>
                <div style={s.cardTitle}>Neuen Kunden anlegen</div>
                <div style={s.row2}>
                  <div><label style={s.label}>Kundenname *</label><input value={neuerKunde.name} onChange={e => setNeuerKunde(k => ({ ...k, name: e.target.value }))} placeholder="Mustermann GmbH" style={s.input} /></div>
                  <div><label style={s.label}>Kundennummer</label><input value={neuerKunde.kundennummer} onChange={e => setNeuerKunde(k => ({ ...k, kundennummer: e.target.value }))} placeholder="K-12345" style={s.input} /></div>
                </div>
                <div style={s.row1}><label style={s.label}>E-Mail Adresse (für Lieferschein)</label><input value={neuerKunde.email || ""} onChange={e => setNeuerKunde(k => ({ ...k, email: e.target.value }))} placeholder="kontakt@mustermann.at" style={s.input} /></div>
                <button style={s.btnPrimary} disabled={!neuerKunde.name} onClick={async () => { try { await apiFetch("/kunden", { method: "POST", body: JSON.stringify(neuerKunde) }); setNeuerKunde({ name: "", kundennummer: "", email: "" }); await loadKunden(); } catch(e) { setError(e.message); } }}>Kunden anlegen</button>
              </div>

              <div style={s.card}>
                <div style={s.cardTitle}>Kundenliste ({kunden.length})</div>
                <input value={kundenSuche} onChange={e => { setKundenSuche(e.target.value); setKundenSeite(0); }} placeholder="Kunde suchen…" style={{ ...s.input, marginBottom: 14 }} />
                {(() => {
                  const gefiltert = kunden.filter(k => k.name.toLowerCase().includes(kundenSuche.toLowerCase()) || k.kundennummer?.includes(kundenSuche));
                  const seiten = Math.ceil(gefiltert.length / KUNDEN_PRO_SEITE);
                  const sichtbar = gefiltert.slice(kundenSeite * KUNDEN_PRO_SEITE, (kundenSeite + 1) * KUNDEN_PRO_SEITE);
                  return (
                    <>
                      {gefiltert.length === 0 && <div style={{ color: "#888", fontSize: 14 }}>Kein Kunde gefunden.</div>}
                      {sichtbar.map(k => (
                        <div key={k.id} style={{ padding: "10px 0", borderTop: "1px solid #f0f2f5" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div>
                              <span style={{ fontWeight: 500 }}>{k.name}</span>
                              {k.kundennummer && <span style={{ fontSize: 12, color: "#888", marginLeft: 8 }}>{k.kundennummer}</span>}
                              {k.email ? <span style={{ fontSize: 12, color: "#2196c4", marginLeft: 8 }}>✉ {k.email}</span> : <span style={{ fontSize: 12, color: "#e67e22", marginLeft: 8 }}>⚠ keine E-Mail</span>}
                            </div>
                            <div style={{ display: "flex", gap: 6 }}>
                              <button style={s.btnSmall} onClick={() => {
                                const email = window.prompt(`E-Mail für ${k.name}:`, k.email || "");
                                if (email === null) return;
                                apiFetch(`/kunden/${k.id}`, { method: "PUT", body: JSON.stringify({ name: k.name, kundennummer: k.kundennummer, email }) }).then(() => loadKunden()).catch(e => setError(e.message));
                              }}>✉ E-Mail</button>
                              <button style={s.btnDanger} onClick={async () => { if (!window.confirm("Kunden löschen?")) return; try { await apiFetch(`/kunden/${k.id}`, { method: "DELETE" }); await loadKunden(); } catch(e) { setError(e.message); } }}>✕</button>
                            </div>
                          </div>
                        </div>
                      ))}
                      {seiten > 1 && (
                        <div style={{ display: "flex", justifyContent: "center", gap: 6, marginTop: 16, flexWrap: "wrap" }}>
                          <button style={s.btnSmall} disabled={kundenSeite === 0} onClick={() => setKundenSeite(p => p - 1)}>← Zurück</button>
                          <span style={{ fontSize: 13, color: "#888", padding: "5px 8px" }}>{kundenSeite + 1} / {seiten}</span>
                          <button style={s.btnSmall} disabled={kundenSeite >= seiten - 1} onClick={() => setKundenSeite(p => p + 1)}>Weiter →</button>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>

              <div style={s.card}>
                <div style={s.cardTitle}>Warentypen ({warentypen.length})</div>
                {warentypen.map(w => (
                  <div key={w.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderTop: "1px solid #f0f2f5" }}>
                    <span style={{ fontSize: 14 }}>{w.name}</span>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button style={{ ...s.btnSmall, borderColor: "#8a9bb0", color: "#8a9bb0" }} onClick={async () => {
                        const neuerName = window.prompt("Warentyp umbenennen:", w.name);
                        if (!neuerName || neuerName.trim() === w.name) return;
                        try {
                          await apiFetch(`/warentypen/${w.id}`, { method: "PUT", body: JSON.stringify({ name: neuerName.trim() }) });
                          await loadWarentypen();
                          await loadEditLog();
                        } catch(e) { setError(e.message); }
                      }}>✎ Umbenennen</button>
                      <button style={s.btnDanger} onClick={async () => {
                        if (!window.confirm(`"${w.name}" löschen?`)) return;
                        try { await apiFetch(`/warentypen/${w.id}`, { method: "DELETE" }); await loadWarentypen(); } catch(e) { setError(e.message); }
                      }}>✕</button>
                    </div>
                  </div>
                ))}
                <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
                  <input value={neuerWarentyp} onChange={e => setNeuerWarentyp(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter" && neuerWarentyp.trim()) { apiFetch("/warentypen", { method: "POST", body: JSON.stringify({ name: neuerWarentyp.trim() }) }).then(() => { setNeuerWarentyp(""); loadWarentypen(); }).catch(e => setError(e.message)); } }}
                    placeholder="z.B. Tablet, Server…" style={{ ...s.input, flex: 1 }} />
                  <button style={s.btnPrimary} disabled={!neuerWarentyp.trim()} onClick={async () => {
                    try { await apiFetch("/warentypen", { method: "POST", body: JSON.stringify({ name: neuerWarentyp.trim() }) }); setNeuerWarentyp(""); await loadWarentypen(); } catch(e) { setError(e.message); }
                  }}>+ Anlegen</button>
                </div>
              </div>

              <div style={s.card}>
                <div style={s.cardTitle}>Schwellwerte</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 80px 44px", gap: 10, marginBottom: 14, alignItems: "end" }}>
                  <div><label style={s.label}>Kunde</label>
                    <select value={neuerSchwellwert.kunde} onChange={e => setNeuerSchwellwert(s => ({ ...s, kunde: e.target.value }))} style={s.input}>
                      <option value="">Wählen…</option>{kunden.map(k => <option key={k.id} value={k.name}>{k.name}</option>)}
                    </select>
                  </div>
                  <div><label style={s.label}>Warentyp</label>
                    <select value={neuerSchwellwert.warentyp} onChange={e => setNeuerSchwellwert(s => ({ ...s, warentyp: e.target.value }))} style={s.input}>
                      <option value="">Wählen…</option>{warentypen.map(w => <option key={w.id} value={w.name}>{w.name}</option>)}
                    </select>
                  </div>
                  <div><label style={s.label}>Min.</label><input type="number" min="1" value={neuerSchwellwert.minbestand} onChange={e => setNeuerSchwellwert(s => ({ ...s, minbestand: e.target.value }))} style={s.input} /></div>
                  <button style={{ ...s.btnPrimary, padding: "9px 10px" }} disabled={!neuerSchwellwert.kunde || !neuerSchwellwert.warentyp || !neuerSchwellwert.minbestand}
                    onClick={async () => { try { await apiFetch("/schwellwerte", { method: "POST", body: JSON.stringify(neuerSchwellwert) }); setNeuerSchwellwert({ kunde: "", warentyp: "", minbestand: "" }); await loadSchwellwerte(); } catch(e) { setError(e.message); } }}>+</button>
                </div>
                {schwellwerte.length === 0 && <div style={{ color: "#888", fontSize: 13 }}>Keine Schwellwerte definiert.</div>}
                {schwellwerte.map(sw => (
                  <div key={sw.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", borderTop: "1px solid #f0f2f5" }}>
                    <span style={{ fontSize: 13 }}><strong>{sw.kunde}</strong> · {sw.warentyp} · min. <strong>{sw.minbestand} Stk</strong></span>
                    <button style={s.btnDanger} onClick={async () => { if (!window.confirm("Löschen?")) return; try { await apiFetch(`/schwellwerte/${sw.id}`, { method: "DELETE" }); await loadSchwellwerte(); } catch(e) { setError(e.message); } }}>✕</button>
                  </div>
                ))}
              </div>

              <div style={s.card}>
                <div style={s.cardTitle}>Gelöschte Buchungen ({geloeschteBuchungen.length})</div>
                {geloeschteBuchungen.length === 0 && <div style={{ color: "#888", fontSize: 14 }}>Keine gelöschten Buchungen.</div>}
                {geloeschteBuchungen.map(b => (
                  <div key={b.id} style={{ padding: "10px 0", borderTop: "1px solid #f0f2f5" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{b.warentyp}{b.warenbezeichnung ? ` – ${b.warenbezeichnung}` : ""}</span>
                        <span style={{ fontSize: 12, color: "#888", marginLeft: 8 }}>{b.kunde} · {b.menge} Stk</span>
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button style={{ ...s.btnSmall, borderColor: "#2d7a11", color: "#2d7a11" }} onClick={async () => {
                          try { await apiFetch(`/buchungen/${b.id}/restore`, { method: "POST" }); await loadGeloeschteBuchungen(); await loadBuchungen(filterKunde, filterSuche); await loadLagerbestand(lagerKunde); } catch(e) { setError(e.message); }
                        }}>↩ Wiederherstellen</button>
                        <button style={s.btnDanger} onClick={async () => {
                          if (!window.confirm("Buchung endgültig löschen? Dies kann nicht rückgängig gemacht werden!")) return;
                          try { await apiFetch(`/buchungen/${b.id}/endgueltig`, { method: "DELETE" }); await loadGeloeschteBuchungen(); } catch(e) { setError(e.message); }
                        }}>✕ Endgültig</button>
                      </div>
                    </div>
                    <div style={{ fontSize: 11, color: "#aaa", marginTop: 2 }}>Gelöscht {new Date(b.geloescht_am).toLocaleString("de-DE")} von {b.geloescht_von_name}</div>
                  </div>
                ))}
              </div>

              <div style={s.card}>
                <div style={s.cardTitle}>Lösch-Log</div>
                {deleteLog.length === 0 && <div style={{ color: "#888", fontSize: 14 }}>Noch keine endgültigen Löschungen.</div>}
                {deleteLog.map(l => (
                  <div key={l.id} style={{ padding: "10px 0", borderTop: "1px solid #f0f2f5" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{l.warentyp}{l.warenbezeichnung ? ` – ${l.warenbezeichnung}` : ""}</span>
                        <span style={{ fontSize: 12, color: "#888", marginLeft: 8 }}>{l.kunde} · {l.menge} Stk</span>
                      </div>
                      <span style={{ fontSize: 11, color: "#aaa" }}>{new Date(l.endgueltig_geloescht_am).toLocaleString("de-DE")}</span>
                    </div>
                    <div style={{ fontSize: 11, color: "#bbb", marginTop: 2 }}>Gelöscht von {l.geloescht_von_name} · Erstellt von {l.bearbeiter_name}</div>
                  </div>
                ))}
              </div>

              <div style={s.card}>
                <div style={s.cardTitle}>Bearbeitungs-Log</div>
                {editLog.length === 0 && <div style={{ color: "#888", fontSize: 14 }}>Noch keine Bearbeitungen.</div>}
                {editLog.map(l => (
                  <div key={l.id} style={{ padding: "10px 0", borderTop: "1px solid #f0f2f5" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 14 }}>{l.buchung_id === 0 ? "Warentyp" : `Buchung #${l.buchung_id}`}</span>
                        <span style={{ fontSize: 12, color: "#888", marginLeft: 8 }}>· {l.feld}: </span>
                        <span style={{ fontSize: 12, color: "#c0392b", textDecoration: "line-through", marginLeft: 4 }}>{l.alter_wert || "—"}</span>
                        <span style={{ fontSize: 12, color: "#888", marginLeft: 4 }}>→</span>
                        <span style={{ fontSize: 12, color: "#2d7a11", marginLeft: 4 }}>{l.neuer_wert || "—"}</span>
                      </div>
                      <span style={{ fontSize: 11, color: "#aaa" }}>{new Date(l.geaendert_am).toLocaleString("de-DE")}</span>
                    </div>
                    <div style={{ fontSize: 11, color: "#bbb", marginTop: 2 }}>Geändert von {l.geaendert_von_name}</div>
                  </div>
                ))}
              </div>

              <div style={s.card}>
                <div style={s.cardTitle}>E-Mail Konfiguration</div>
                <div style={s.row1}><label style={s.label}>Absender</label><input value={mailConfig.absender} onChange={e => setMailConfig(c => ({ ...c, absender: e.target.value }))} style={s.input} /></div>
                <div style={s.row1}>
                  <label style={s.label}>Empfänger</label>
                  <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
                    {["admins", "custom"].map(m => (
                      <button key={m} style={{ ...s.btn, background: mailConfig.empfaengerModus === m ? BLUE : "#fff", color: mailConfig.empfaengerModus === m ? "#fff" : "#444", borderColor: mailConfig.empfaengerModus === m ? BLUE : "#dde1e7" }}
                        onClick={() => setMailConfig(c => ({ ...c, empfaengerModus: m }))}>
                        {m === "admins" ? "Alle Lager-Admins" : "Eigene Adressen"}
                      </button>
                    ))}
                  </div>
                  {mailConfig.empfaengerModus === "admins" && (
                    <div style={{ fontSize: 13, color: "#666", padding: "10px 14px", background: "#f8fafc", borderRadius: 8, border: "1px solid #eef0f3" }}>
                      {adminEmails.length > 0 ? <>Mails gehen an: {adminEmails.join(", ")}</> : "Alle Mitglieder der Lager-Admins Gruppe."}
                    </div>
                  )}
                  {mailConfig.empfaengerModus === "custom" && (
                    <div>
                      <input value={mailConfig.empfaengerInput} onChange={e => setMailConfig(c => ({ ...c, empfaengerInput: e.target.value, empfaenger: e.target.value.split(/[,;]/).map(x => x.trim()).filter(x => x.includes("@")) }))} placeholder="florian@artaker.it; max@artaker.it" style={s.input} />
                      <div style={{ fontSize: 12, color: "#888", marginTop: 4 }}>{mailConfig.empfaenger.length} Empfänger · Komma oder Semikolon als Trenner</div>
                    </div>
                  )}
                </div>
                <div style={s.row2}>
                  <div>
                    <label style={s.label}>CSV-Versand — Tag des Monats</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <input type="number" min="0" max="31" value={mailConfig.csvVersandTag} onChange={e => setMailConfig(c => ({ ...c, csvVersandTag: e.target.value }))} placeholder="0" style={{ ...s.input, width: 70 }} />
                      <span style={{ fontSize: 12, color: "#888" }}>{mailConfig.csvVersandTag > 0 ? `Jeden ${mailConfig.csvVersandTag}. um 07:00 UTC` : "0 = deaktiviert"}</span>
                    </div>
                  </div>
                  <div>
                    <label style={s.label}>Aufbewahrung gelöschter Buchungen</label>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <input type="number" min="1" value={mailConfig.softDeleteTage} onChange={e => setMailConfig(c => ({ ...c, softDeleteTage: e.target.value }))} style={{ ...s.input, width: 70 }} />
                      <span style={{ fontSize: 12, color: "#888" }}>Tage</span>
                    </div>
                  </div>
                </div>
                <div style={{ display: "flex", gap: 10, marginTop: 4, flexWrap: "wrap" }}>
                  <button style={s.btnPrimary} onClick={async () => {
                    try {
                      await apiFetch("/konfiguration", { method: "POST", body: JSON.stringify({ mailAbsender: mailConfig.absender, mailEmpfaenger: JSON.stringify(mailConfig.empfaenger), mailEmpfaengerModus: mailConfig.empfaengerModus, csvVersandTag: mailConfig.csvVersandTag || "0", softDeleteTage: mailConfig.softDeleteTage || "30" }) });
                      alert("✅ Gespeichert!");
                    } catch(e) { setError(e.message); }
                  }}>Speichern</button>
                  <button style={s.btn} onClick={async () => { try { await apiFetch("/testmail", { method: "POST", body: JSON.stringify({ absender: mailConfig.absender, empfaenger: [user?.email], modus: "custom" }) }); alert("✅ Test-Mail an dich gesendet!"); } catch(e) { setError(e.message); } }}>Test (nur ich)</button>
                  <button style={s.btn} onClick={async () => { try { const emp = mailConfig.empfaengerModus === "admins" ? [] : [...new Set([...mailConfig.empfaenger, user?.email])]; await apiFetch("/testmail", { method: "POST", body: JSON.stringify({ absender: mailConfig.absender, empfaenger: emp, modus: mailConfig.empfaengerModus }) }); alert("✅ Test-Mail gesendet!"); } catch(e) { setError(e.message); } }}>Test (alle)</button>
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
}
